import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconCopy, IconEye, IconLock, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';

import type { PermissionGroupDto } from '@shared/services/apiService';

import { isBuiltIn } from '../model/privileges';
import type { AccessControlApi } from '../model/useAccessControlApi';
import { DeleteGroupDialog } from './DeleteGroupDialog';
import { GroupDialog, GroupDialogMode } from './GroupDialog';
import { useStyles } from './styles';

interface GroupsPanelProps {
  api: AccessControlApi;
  /** Opens the users of one group. */
  onShowMembers: (groupId: string) => void;
}

export const GroupsPanel: React.FC<GroupsPanelProps> = ({ api, onShowMembers }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [dialog, setDialog] = useState<{
    mode: GroupDialogMode;
    group: PermissionGroupDto | null;
  }>();
  const [toDelete, setToDelete] = useState<PermissionGroupDto | null>(null);

  // Only known when the caller may list users; otherwise the column is left out.
  const showMembers = api.canManageUsers;
  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    api.users.forEach((user) =>
      user.groups.forEach((group) => counts.set(group.id, (counts.get(group.id) ?? 0) + 1)),
    );
    return counts;
  }, [api.users]);
  const hasCustomGroups = api.groups.some((group) => !isBuiltIn(group));

  if (api.isGroupsLoading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.panelHeader}>
        <Typography variant="body2" color="text.secondary">
          {t('accessControl.groups.description')}
        </Typography>
        {api.canManageGroups && (
          <Button
            variant="contained"
            startIcon={<IconPlus size={18} />}
            onClick={() => setDialog({ mode: 'create', group: null })}
            data-testid="permission-group-create">
            {t('accessControl.groups.create')}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('accessControl.groups.columns.name')}</TableCell>
              <TableCell>{t('accessControl.groups.columns.privileges')}</TableCell>
              {showMembers && <TableCell>{t('accessControl.groups.columns.members')}</TableCell>}
              <TableCell>{t('accessControl.groups.columns.directory')}</TableCell>
              <TableCell align="right">{t('accessControl.groups.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {api.groups.map((group) => {
              const builtIn = isBuiltIn(group);
              const mapped = group.directoryGroups.length > 0;
              const canEdit = api.canManageGroups && !builtIn;

              return (
                <TableRow key={group.id} hover data-testid={`permission-group-row-${group.id}`}>
                  <TableCell>
                    <Box className={classes.nameCell}>
                      <Typography fontWeight={600}>{group.name}</Typography>
                      {builtIn && (
                        <Tooltip title={t('accessControl.groups.builtInTooltip')}>
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<IconLock size={14} />}
                            label={t('accessControl.builtIn')}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {t('accessControl.groups.privilegeCount', { count: group.privileges.length })}
                  </TableCell>
                  {showMembers && (
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onShowMembers(group.id)}
                        data-testid={`permission-group-members-${group.id}`}>
                        {memberCounts.get(group.id) ?? 0}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box className={classes.chips}>
                      {group.directoryGroups.map((value) => (
                        <Chip
                          key={value}
                          size="small"
                          color="info"
                          variant="outlined"
                          label={value}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actions}>
                      <Tooltip
                        title={t(
                          canEdit ? 'accessControl.common.edit' : 'accessControl.common.view',
                        )}>
                        <IconButton
                          size="small"
                          onClick={() => setDialog({ mode: canEdit ? 'edit' : 'view', group })}>
                          {canEdit ? <IconPencil size={18} /> : <IconEye size={18} />}
                        </IconButton>
                      </Tooltip>
                      {api.canManageGroups && (
                        <Tooltip title={t('accessControl.groups.duplicate')}>
                          <IconButton
                            size="small"
                            onClick={() => setDialog({ mode: 'duplicate', group })}>
                            <IconCopy size={18} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip
                          title={
                            mapped
                              ? t('accessControl.groups.deleteBlockedByDirectory')
                              : t('accessControl.common.delete')
                          }>
                          {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={mapped}
                              onClick={() => setToDelete(group)}>
                              <IconTrash size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {!hasCustomGroups && (
              <TableRow>
                <TableCell colSpan={showMembers ? 5 : 4} className={classes.emptyState}>
                  <Typography variant="body2" gutterBottom>
                    {t('accessControl.groups.empty.title')}
                  </Typography>
                  <Typography variant="caption">
                    {t(
                      api.canManageGroups
                        ? 'accessControl.groups.empty.hint'
                        : 'accessControl.groups.empty.hintReadOnly',
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <GroupDialog
        open={Boolean(dialog)}
        mode={dialog?.mode ?? 'create'}
        group={dialog?.group ?? null}
        api={api}
        onClose={() => setDialog(undefined)}
      />
      <DeleteGroupDialog
        group={toDelete}
        memberCount={toDelete ? memberCounts.get(toDelete.id) : undefined}
        api={api}
        onClose={() => setToDelete(null)}
      />
    </>
  );
};
